import "./Drivers.scss";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TransporterService from "../../services/TransporterService";
import { useLoader } from "../../contexts/LoaderContext";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableComponent from "../../components/TableComponent/TableComponent";
import StatsBoxContainer from "../../components/StatsBoxContainer/StatsBoxContainer";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import { transformTruckNumber } from "../../utils/general";
import { SelectChangeEvent } from "@mui/material";
import UpdateTruckModal from "../../components/Jobs/UpdateTruckModal";

interface Driver {
  driver: {
    id: number;
    name: string;
  };
  transporter: {
    id: number;
    uniqueId: string;
    name: string;
  };
  currentTruck: {
    id: number;
    name: string;
  };
  available: boolean;
}

interface DriverStats {
  totalDrivers: number;
  availableDrivers: number;
  assignedDrivers: number;
}

function Drivers() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(0);
  const REFRESH_COOLDOWN = 2000; // 2 seconds cooldown between refreshes
  const [driversLoaded, setDriversLoaded] = useState(false);
  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const [driverStats, setDriverStats] = useState<DriverStats>({
    totalDrivers: 0,
    availableDrivers: 0,
    assignedDrivers: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [updateTruckModalOpen, setUpdateTruckModalOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const modalStateRef = useRef({
    isOpen: false,
    driverId: null as number | null,
  });

  // Get the selected driver from the current drivers array
  const selectedDriver = useMemo(() => {
    if (!selectedDriverId || !drivers) return null;
    const found = drivers.find((d) => d.driver.id === selectedDriverId);
    console.log("Selected driver lookup:", {
      selectedDriverId,
      found: !!found,
      driversCount: drivers.length,
    });
    return found || null;
  }, [selectedDriverId, drivers]);

  const fetchDrivers = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      const now = Date.now();
      if (!forceRefresh && now - lastRefreshTime.current < REFRESH_COOLDOWN) {
        console.log("Refresh skipped - too soon since last refresh");
        if (!suppressLoader) {
          hideLoader();
        } else {
          loader.setLoading(false);
        }
        return;
      }

      // Prevent multiple simultaneous calls
      if (isLoading.current) {
        console.log("Fetch already in progress, skipping");
        return;
      }

      lastRefreshTime.current = now;
      isLoading.current = true;

      try {
        if (!initialLoadDone.current) {
          setDriversLoaded(false);
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        const response = await TransporterService.getAllDrivers();

        if (!response?.data?.error && Array.isArray(response?.data?.data)) {
          const drivers: Driver[] = response.data.data;
          setDrivers(drivers);

          // Calculate stats
          const stats: DriverStats = {
            totalDrivers: drivers.length,
            availableDrivers: drivers.filter((d) => d.available).length,
            assignedDrivers: drivers.filter((d) => !d.available).length,
          };
          setDriverStats(stats);
        } else {
          console.error(
            "Error in fetching drivers:",
            response?.data?.message || "Invalid response format"
          );
          setError(response?.data?.message || "Error fetching drivers");
          setDrivers([]); // Reset drivers to empty array on error
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setDriversLoaded(true);
        initialLoadDone.current = true;
        isLoading.current = false;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [] // Remove dependencies to prevent recreation
  );

  // Memoize table data transformation
  const tableData = useMemo(() => {
    if (!drivers) return [];

    return drivers
      .map((driver) => ({
        id: driver.driver.id,
        name: driver.driver.name,
        transporter: driver.transporter.name,
        transporterId: driver.transporter.uniqueId,
        currentTruck: transformTruckNumber(driver.currentTruck.name),
        status: driver.available ? "Available" : "Busy",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [drivers]);

  // Memoize filtered and paginated data
  const filteredAndPaginatedData = useMemo(() => {
    let filteredData = tableData;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(
        (row) =>
          row.name.toLowerCase().includes(query) ||
          row.transporter.toLowerCase().includes(query) ||
          row.currentTruck.toLowerCase().includes(query)
      );
    }

    // Calculate pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      data: filteredData.slice(startIndex, endIndex),
      totalElements: filteredData.length,
      totalPages: Math.ceil(filteredData.length / pageSize),
    };
  }, [tableData, searchQuery, currentPage, pageSize]);

  // Memoize search suggestions
  const searchSuggestionsMemo = useMemo(() => {
    if (tableData.length > 0) {
      const suggestions = new Set<string>();

      tableData.forEach((row) => {
        if (row.name) suggestions.add(row.name);
        if (row.transporter) suggestions.add(row.transporter);
        if (row.currentTruck) suggestions.add(row.currentTruck);
      });

      return Array.from(suggestions);
    }
    return [];
  }, [tableData]);

  // Memoize columns definition
  const columns = useMemo(
    () => [
      // {
      //   header: "Driver ID",
      //   accessor: "id",
      //   sortable: true,
      //   centerAlign: true,
      //   width: "100px",
      // },
      {
        header: "Driver Name",
        accessor: "name",
        sortable: true,
      },
      {
        header: "Current Truck Number",
        accessor: "currentTruck",
        sortable: true,
      },
      {
        header: "Transporter Name",
        accessor: "transporter",
        sortable: true,
      },

      {
        header: "Status",
        accessor: "status",
        sortable: true,
        centerAlign: true,
        width: "150px",
        cell: (value: string) => {
          return (
            <span
              className={`px-2 py-1 rounded-full text-sm w-[100px] inline-block ${
                value === "Available"
                  ? "bg-green-100 text-green-800"
                  : "bg-orange-100 text-orange-500"
              }`}
            >
              {value}
            </span>
          );
        },
      },
    ],
    []
  );

  // Memoize stats data
  const statsData = useMemo(() => {
    // Use filtered data if search is active, otherwise use all data
    const dataToUse = searchQuery ? filteredAndPaginatedData.data : tableData;

    const totalDrivers = dataToUse.length;
    const availableDrivers = dataToUse.filter(
      (d) => d.status === "Available"
    ).length;
    const assignedDrivers = dataToUse.filter((d) => d.status === "Busy").length;

    return [
      {
        name: "Total",
        value: totalDrivers,
        valueColor: "#59585B",
      },
      {
        name: "Available",
        value: availableDrivers,
        valueColor: "#10B981",
      },
      {
        name: "Busy",
        value: assignedDrivers,
        valueColor: "#FF9F1C",
      },
    ];
  }, [searchQuery, filteredAndPaginatedData.data, tableData]);

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1); // Reset to first page when changing page size
    },
    []
  );

  const handleUpdateTruck = useCallback((driver: Driver) => {
    setSelectedDriverId(driver.driver.id);
    setUpdateTruckModalOpen(true);
    modalStateRef.current = { isOpen: true, driverId: driver.driver.id };
  }, []);

  const handleUpdateTruckModalClose = useCallback(() => {
    setSelectedDriverId(null);
    setUpdateTruckModalOpen(false);
  }, []);

  const handleUpdateTruckSuccess = useCallback(() => {
    // Don't force refresh immediately, let the auto-refresh handle it
    // fetchDrivers(true, true);
    handleUpdateTruckModalClose();
  }, [handleUpdateTruckModalClose]);

  // Memoize table actions
  const tableActions = useMemo(
    () => [
      {
        label: "Update Truck Number",
        callback: (row: Record<string, any>) => {
          // Find the original driver data
          const driver = drivers.find((d) => d.driver.id === row.id);
          if (driver) {
            handleUpdateTruck(driver);
          }
        },
        isEnabled: (row: Record<string, any>) =>
          row.status !== "Busy" && row.currentTruck !== "N/A",
      },
    ],
    [drivers, handleUpdateTruck]
  );

  // Initial data fetch and auto-refresh setup
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchDrivers(false);
      } catch (error) {
        console.error("Error in initial fetch:", error);
      }
    };

    fetchData();

    return () => {
      // Cleanup function
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = null;
      }
    };
  }, []); // Only run once on mount

  // Separate effect for auto-refresh
  useEffect(() => {
    if (autoRefreshEnabled) {
      autoRefreshInterval.current = setInterval(() => {
        fetchDrivers(true);
      }, 30000); // Refresh every 30 seconds
    } else {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = null;
      }
    }

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = null;
      }
    };
  }, [autoRefreshEnabled]); // Remove fetchDrivers dependency

  // Update search suggestions when table data changes
  useEffect(() => {
    setSearchSuggestions(searchSuggestionsMemo);
  }, [searchSuggestionsMemo]);

  // Close modal if selected driver is no longer available after refresh
  useEffect(() => {
    console.log("Modal state effect triggered:", {
      updateTruckModalOpen,
      selectedDriverId,
      hasSelectedDriver: !!selectedDriver,
      modalStateRef: modalStateRef.current,
    });

    // Only check if modal is actually open according to ref
    if (modalStateRef.current.isOpen && selectedDriverId && !selectedDriver) {
      console.log("Selected driver no longer available, closing modal");
      handleUpdateTruckModalClose();
    } else if (
      modalStateRef.current.isOpen &&
      selectedDriverId &&
      selectedDriver
    ) {
      console.log("Selected driver found after refresh, modal remains open");
    }
  }, [
    updateTruckModalOpen,
    selectedDriverId,
    selectedDriver,
    handleUpdateTruckModalClose,
  ]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled((prev) => !prev);
  }, []);

  // Handle manual refresh
  const handleManualRefresh = useCallback(() => {
    fetchDrivers(true, true);
  }, [fetchDrivers]);

  // Handle search input changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <div className="drivers-page p-[20px]">
      <StatsBoxContainer stats={statsData} />

      <div className="flex justify-between items-center py-[30px]">
        <SearchBar
          placeholder="Search"
          onSearch={handleSearchChange}
          showClearButton={true}
          suggestions={searchSuggestions}
          onSuggestionClick={(suggestion) => {
            setSearchQuery(suggestion);
            setCurrentPage(1); // Reset to first page on suggestion click
          }}
        />
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className={`secondary_button ${
                loader.isLoading ? "opacity-70" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={loader.isLoading}
            >
              <div className="flex items-center">
                {loader.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  "Refresh"
                )}
              </div>
            </button>
            <button
              type="button"
              className={`secondary_button ${
                autoRefreshEnabled ? "active" : ""
              }`}
              onClick={toggleAutoRefresh}
              title={
                autoRefreshEnabled
                  ? "Auto-refresh enabled (every 30s)"
                  : "Auto-refresh disabled"
              }
            >
              <div className="flex items-center">
                {autoRefreshEnabled ? "Auto-refresh On" : "Auto-refresh Off"}
              </div>
            </button>
          </div>
        </div>
      </div>

      {driversLoaded && !loader.isLoading && tableData.length === 0 ? (
        <div className="flex justify-center items-center">
          <p className="text-gray-500">No drivers found</p>
        </div>
      ) : (
        <div className="table">
          <TableComponent
            showPagination={true}
            columns={columns}
            data={tableData}
            searchQuery={searchQuery}
            showCheckboxes={false}
            uniqueAccessor="id"
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={Math.ceil(tableData.length / pageSize)}
            totalElements={tableData.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableClientSideFiltering={true}
            actions={tableActions}
          />
        </div>
      )}

      {(() => {
        if (updateTruckModalOpen && selectedDriver) {
          console.log(
            "Rendering modal for driver:",
            selectedDriver.driver.name
          );
          return (
            <UpdateTruckModal
              isOpen={updateTruckModalOpen}
              onClose={handleUpdateTruckModalClose}
              truckData={{
                jobBreakupDetailId: 0, // Not needed for driver update
                currentTruckNumber: selectedDriver.currentTruck.name,
                driverId: selectedDriver.driver.id,
                transporterId: selectedDriver.transporter.id,
                driverName: selectedDriver.driver.name,
                transporterName: selectedDriver.transporter.name,
              }}
              onUpdateSuccess={handleUpdateTruckSuccess}
            />
          );
        }
        return null;
      })()}
    </div>
  );
}

export default Drivers;
